import styled from 'styled-components';
import { Pages } from '../../assets/ts/customTypes';
import { MainContainer } from '../../assets/ts/layout/main';
import { PageTitle } from '../components/PageTitle';
import { ArrowContentBox, ArrowContentBoxFooter, ArrowContentBoxInner, ArrowContentBoxQuote, ArrowContentBoxEmail, ArrowContentBoxEmailLink, ArrowContentBoxIndended } from '../components/ArrowContentBox';
import { COLOR_CI_BLUE } from '../../config';
import { NameTag } from '../components/NameTag';
import { SingleHexagonImage } from '../components/SingleHexagonImage';
import { SolidHexagon } from '../components/SolidHexagon';
import { ListItem } from '../components/ListItem';
import { LandingpageButton } from '../components/LandingpageButton';
import { FooterContainer } from '../components/FooterContainer';
import { defaultFadeInAnimation, defaultSlideLeftAnimation, defaultSlideUpAnimation } from '../utils/AnimationUtils';
import { useLanguage } from '../contexts/LanguageContext';

const ContactHeadline = styled.h2`
    color: ${COLOR_CI_BLUE};
    margin: 0;
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
    font-weight: 400;
`;

const ContactStepsList = styled.ul`
    padding: 0;
`;

const ContactText = styled.div`
    color: #666;
`;

const ContactPageContainer = styled(MainContainer)`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const ImageContainer = styled.div`
    position: relative;
    align-self: flex-start;

    ${SolidHexagon} {
        position: absolute;
        right: 0;
        bottom: 2rem;
    }

    ${SingleHexagonImage} {
        position: relative;
    }
`;

const CenterContainer = styled.div`
    width: 29rem;

    ${NameTag} {
        margin-bottom: 1.5rem;
        width: 20rem;
    }

    ${ArrowContentBoxInner} {
        display: flex;
        flex-direction: column;
    }
`;

const FlexFiller = styled.div`
    flex: 1;
`;

const PageContainer = styled.div`
    display: flex;
    gap: 4rem;
    align-items: flex-end;
    justify-content: center;

    ${ArrowContentBox} {
        height: 28rem;
    }

    #contact-quotes {
        animation: ${defaultFadeInAnimation(0.15)}, ${defaultSlideUpAnimation(0.15)};
    }

    #contact-steps {
        width: 25rem;
        animation: ${defaultFadeInAnimation(0.3)}, ${defaultSlideUpAnimation(0.3)};
    }

    ${ImageContainer} {
        animation: ${defaultFadeInAnimation()}, ${defaultSlideUpAnimation()};
    }

    ${NameTag} {
        animation: ${defaultFadeInAnimation(0.45)}, ${defaultSlideLeftAnimation(0.45)};
    }
`;

export function ContactPage(props: {
    page: Pages;
    onChangePage: (page: Pages) => void;
}) {
    const [language] = useLanguage();
    const page = language.contactPage;

    return (
        <ContactPageContainer>
            <PageTitle width={page.headlineWidth}>{page.headline}</PageTitle>
            <PageContainer>
                <ImageContainer>
                    <SolidHexagon width='17rem' height='20rem' />

                    <SingleHexagonImage width='25rem' height='27rem' imageUrl={page.imageUrl} borderWidth={3} />
                </ImageContainer>

                <CenterContainer>
                    {page.name && <NameTag>{page.name}</NameTag>}

                    <ArrowContentBox arrowTop='5rem' arrowLeft={0} id='contact-quotes'>
                        <ArrowContentBoxInner>
                            {page.texts.map((quote, index) => (
                                <ArrowContentBoxIndended key={index.toString()}>{quote}</ArrowContentBoxIndended>
                            ))}
                            <ArrowContentBoxEmailLink href='mailto:omniplus-eprocurement@daimlertruck.com' target='_top'>
                                <ArrowContentBoxEmail>{page.email}</ArrowContentBoxEmail>
                            </ArrowContentBoxEmailLink>
                            {page.job && (
                                <>
                                    <FlexFiller />
                                    <ArrowContentBoxFooter>{page.job}</ArrowContentBoxFooter>
                                </>
                            )}
                        </ArrowContentBoxInner>
                    </ArrowContentBox>
                </CenterContainer>

                <ArrowContentBox arrowTop='5rem' arrowLeft={0} id='contact-steps'>
                    <ArrowContentBoxInner>
                        <ContactHeadline>{page.stepsHeadline}</ContactHeadline>

                        <ContactStepsList>
                            {page.steps.map((step) => (
                                <ListItem key={step}>{step}</ListItem>
                            ))}
                        </ContactStepsList>
                        <ContactText>{page.text}</ContactText>
                    </ArrowContentBoxInner>
                </ArrowContentBox>
            </PageContainer>

            <FooterContainer>
                <LandingpageButton width={340} onClick={() => props.onChangePage(Pages.LANDINGPAGE)} arrow='left'>
                    {language.backToLandingPageLabel}
                </LandingpageButton>
            </FooterContainer>
        </ContactPageContainer>
    );
}
